import { useEffect, useState } from "react";
import { Loader2, Play, Sparkles, Square } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { useLanguage } from "../i18n";

// Dashboard consumi AI: chiamate reali per flusso (KYC, chatbot, blog, attrazioni)
// con stima di costo e conteggio delle risposte GRATUITE (FAQ/cache/stock) che
// prima sarebbero costate una chiamata AI — il risparmio in numeri.
// In cima: interruttore rapido "Riattiva/Ferma AI" (generazione blog automatica).
export function AdminAiUsage() {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoOn, setAutoOn] = useState(null);
  const [maxDay, setMaxDay] = useState(2);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/ai-usage")
      .then(({ data }) => setData(data.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
    api.get("/admin/settings")
      .then(({ data }) => {
        setAutoOn(!!data.data?.blog_auto_enabled);
        setMaxDay(Number(data.data?.blog_max_per_day ?? 2));
      })
      .catch(() => {});
  }, []);

  const setAuto = async (on) => {
    setBusy(true);
    try {
      await api.post("/admin/settings", { blog_auto_enabled: on });
      if (on) {
        // riattiva = interruttore ON + ripartenza del ciclo di generazione
        await api.post("/admin/blog/ai-batch", { count: 100, with_images: true, auto: true }, { timeout: 30000 });
      } else {
        await api.post("/admin/blog/ai-batch/stop");
      }
      setAutoOn(on);
      toast.success(t(on ? "ai.reactivated" : "ai.deactivated"));
    } catch {
      toast.error(t("misc.error"));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="mt-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#FF6B35]" /></div>;
  if (!data) return null;

  const th = "text-left font-mono-t text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 px-4 py-2.5";
  const td = "px-4 py-3 text-sm text-slate-700";
  const totals = data.totals || {};

  return (
    <div data-testid="admin-ai-usage" className="mt-10 space-y-6">
      {/* interruttore rapido generazione AI */}
      <div data-testid="ai-master-switch" className={`rounded-3xl border p-5 flex flex-wrap items-center gap-4 ${autoOn ? "border-emerald-200 bg-emerald-50/60" : "border-slate-200 bg-white"}`}>
        <div className="flex-1 min-w-[220px]">
          <div className="font-mono-t text-[9px] uppercase tracking-widest text-slate-400">{t("ai.control")}</div>
          <div data-testid="ai-auto-state" className={`mt-1 font-display font-black text-xl ${autoOn ? "text-emerald-600" : "text-slate-500"}`}>
            {autoOn === null ? "…" : autoOn ? t("ai.autoOn") : t("ai.autoOff")}
          </div>
          <p className="text-xs text-slate-400 mt-0.5">{t("ai.capNote").replace("{n}", maxDay)}</p>
        </div>
        {autoOn ? (
          <button data-testid="ai-deactivate" onClick={() => setAuto(false)} disabled={busy || autoOn === null}
            className="rounded-full bg-red-500 text-white px-6 py-3 font-mono-t text-[11px] font-bold uppercase tracking-widest hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center gap-2">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Square className="w-3.5 h-3.5" />} {t("ai.deactivate")}
          </button>
        ) : (
          <button data-testid="ai-reactivate" onClick={() => setAuto(true)} disabled={busy || autoOn === null}
            className="rounded-full bg-[#0C0D0E] text-white px-6 py-3 font-mono-t text-[11px] font-bold uppercase tracking-widest hover:bg-[#FF6B35] transition-colors disabled:opacity-50 flex items-center gap-2">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />} {t("ai.reactivate")}
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="rounded-3xl border border-slate-200 bg-white p-5">
          <div className="font-mono-t text-[9px] uppercase tracking-widest text-slate-400">{t("ai.callsToday")}</div>
          <div data-testid="ai-total-today" className="mt-1 font-display font-black text-3xl text-slate-900">{totals.today ?? 0}</div>
        </div>
        <div className="rounded-3xl border border-slate-200 bg-white p-5">
          <div className="font-mono-t text-[9px] uppercase tracking-widest text-slate-400">{t("ai.callsMonth")}</div>
          <div data-testid="ai-total-month" className="mt-1 font-display font-black text-3xl text-slate-900">{totals.month ?? 0}</div>
        </div>
        <div className="rounded-3xl border border-slate-200 bg-white p-5">
          <div className="font-mono-t text-[9px] uppercase tracking-widest text-slate-400">{t("ai.estCost")}</div>
          <div data-testid="ai-est-cost" className="mt-1 font-display font-black text-3xl text-slate-900">€ {Number(totals.est_cost_eur || 0).toFixed(3)}</div>
        </div>
        <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-5">
          <div className="font-mono-t text-[9px] uppercase tracking-widest text-emerald-600">{t("ai.freeSaved")}</div>
          <div data-testid="ai-free-saved" className="mt-1 font-display font-black text-3xl text-emerald-700">{totals.free_served_month ?? 0}</div>
        </div>
      </div>

      <div className="rounded-3xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#FF6B35]" />
          <span className="font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-500">{t("ai.byFlow")}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className={th}>{t("ai.flow")}</th>
                <th className={th}>{t("ai.model")}</th>
                <th className={th}>{t("ai.today")}</th>
                <th className={th}>{t("ai.week")}</th>
                <th className={th}>{t("ai.month")}</th>
                <th className={th}>{t("ai.estCost")}</th>
              </tr>
            </thead>
            <tbody>
              {(data.flows || []).map((f) => (
                <tr key={f.flow} data-testid={`ai-flow-${f.flow}`} className={`border-t border-slate-100 ${f.flow.endsWith("_free") ? "bg-emerald-50/50" : ""}`}>
                  <td className={td}>{f.label}</td>
                  <td className={`${td} font-mono-t text-xs text-slate-500`}>{(f.models || []).join(", ")}</td>
                  <td className={td}>{f.today}</td>
                  <td className={td}>{f.week}</td>
                  <td className={td}>{f.month}</td>
                  <td className={`${td} font-mono-t text-xs`}>{f.flow.endsWith("_free") ? "€ 0" : `€ ${Number(f.est_cost_eur).toFixed(3)}`}</td>
                </tr>
              ))}
              {(!data.flows || data.flows.length === 0) && (
                <tr><td colSpan={6} className="px-5 py-8 text-center text-sm text-slate-400">{t("ai.empty")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-xs text-slate-400">{t("ai.note")}</p>
    </div>
  );
}
